import { SensorStatus } from "@sitescene/types";

export type SensorDurationLabel = {
	color: string;
	text: string;
};

/**
 * Formats a millisecond duration as a human-readable string,
 * e.g. "2 days 4 hours 2 minutes", "4 hours 2 minutes", or "17 minutes".
 */
export const formatElapsedMs = (ms: number): string => {
	const totalMinutes = Math.floor(ms / (1000 * 60));
	const days = Math.floor(totalMinutes / (60 * 24));
	const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
	const minutes = totalMinutes % 60;

	const parts: string[] = [];
	if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
	if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
	if (minutes > 0 || parts.length === 0)
		parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);

	return parts.join(" ");
};

/**
 * Status-specific duration label for a sensor popup, matching dashboard map copy:
 * - Active:  "Last responded at 6:45pm"
 * - Warning: "Not responded for 28 minutes"
 * - Offline: "Offline for 4 hours 19 minutes"
 */
export const getSensorDurationLabel = (
	status: SensorStatus | string | undefined,
	lastInputReceived: Date | null | string | undefined,
): null | SensorDurationLabel => {
	if (!lastInputReceived) return null;

	const parsedDate = new Date(lastInputReceived);
	if (Number.isNaN(parsedDate.getTime())) return null;

	const elapsedMs = Date.now() - parsedDate.getTime();
	if (elapsedMs < 0) return null;

	const normalized = String(status).toLowerCase();

	if (normalized === SensorStatus.Active || normalized === "active") {
		const timeStr = parsedDate.toLocaleTimeString("en-US", {
			hour: "numeric",
			hour12: true,
			minute: "2-digit",
		});
		return {
			color: "text-utility-green-700",
			text: `Last responded at ${timeStr.toLowerCase()}`,
		};
	}

	const duration = formatElapsedMs(elapsedMs);

	if (normalized === SensorStatus.Warning || normalized === "warning") {
		return {
			color: "text-utility-yellow-600",
			text: `Not responded for ${duration}`,
		};
	}

	if (normalized === SensorStatus.Offline || normalized === "offline") {
		return {
			color: "text-utility-red-600",
			text: `Offline for ${duration}`,
		};
	}

	return null;
};
